import { useState, useMemo, useEffect } from "react";
import { useDashboardViewMode } from "@/hooks/useDashboardViewMode";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { useI18n } from "@/hooks/useI18n";
import {
  TravelRequestCard,
  TravelRequestsTable,
  SortTravelRequestButton,
  ViewTravelRequestButton,
  FilterTravelRequestButton,
  ClientRequestModal,
  type SortMode,
} from "@/lib/requests";
import { DashboardTravelRequest } from "@/lib/models/dashboard";
import { canEditRequest } from "@/lib/requests/models/client-request-form.model";
import { needsAgencyAssignment } from "@/lib/requests/TravelRequestButton";
import { useChosenItineraryPreview } from "@/lib/requests/hooks/useChosenItineraryPreview";
import { useAuth } from "@/hooks/useAuth";
import { useDashboardTravelRequestCounts, useDashboardTravelRequestsPage } from "@/hooks/useDashboardLists";
import { StatusFilter, type StatusFilterTone } from "@/components/ui/status-filter";
import { ListPagination } from "@/components/ui/list-pagination";
import { TravelRequestCardSkeleton } from "@/lib/requests/TravelRequestCardSkeleton";
import { FilterConfig, FilterOption } from "@/components/ui/filter-dropdown";
import type {
  DashboardTravelRequestFilter,
  DashboardTravelRequestPhase,
} from "@/integrations/supabase/rpc-types";
import type { TranslationKey } from "@/types/i18n.types";
import { DASHBOARD_PAGE_SIZE, RequestsTabProps } from "./types";

// I nomi dei campi si traducono; gli operatori sono simboli e restano letterali.
const buildFilterOptions = (t: ReturnType<typeof useI18n>['t']): FilterOption[] => {
  const contains = [{ value: 'contains', label: t('dashboard.toolbar.opContains') }];
  return [
    { value: 'destinations', label: t('dashboard.toolbar.fieldDestinations'), operations: contains },
    { value: 'customer_name', label: t('dashboard.toolbar.fieldCustomerName'), operations: contains },
    {
      value: 'budget', label: t('dashboard.toolbar.fieldBudget'),
      operations: [
        { value: 'greater_than', label: '>' }, { value: 'less_than', label: '<' },
        { value: 'greater_equal', label: '>=' }, { value: 'less_equal', label: '<=' }
      ]
    },
    {
      value: 'guests', label: t('dashboard.toolbar.fieldGuests'),
      operations: [
        { value: 'equals', label: '=' }, { value: 'greater_than', label: '>' },
        { value: 'less_than', label: '<' }, { value: 'greater_equal', label: '>=' },
        { value: 'less_equal', label: '<=' }
      ]
    },
    { value: 'departure', label: t('dashboard.toolbar.fieldDeparture'), operations: contains },
    { value: 'travel_start_date', label: t('dashboard.toolbar.fieldTravelStartDate'), is_date: true, operations: [] },
    { value: 'travel_end_date', label: t('dashboard.toolbar.fieldTravelEndDate'), is_date: true, operations: [] },
    { value: 'created_at', label: t('dashboard.toolbar.fieldCreatedAt'), is_date: true, operations: [] },
  ];
};

/** 'all' = tutta la vista. */
type RequestPhaseFilter = 'all' | DashboardTravelRequestPhase;

type PhaseOption = { value: RequestPhaseFilter; labelKey: TranslationKey; tone: StatusFilterTone };

/**
 * Pillole della vista attive. Admin e agenzia distribuiscono il lavoro: a loro serve
 * sapere cosa va ancora girato a qualcuno. Il consulente vede solo richieste gia' sue,
 * quindi per lui conta cosa e' arrivato da poco (ultimi 7 giorni). A tutti serve
 * vedere le partenze dei prossimi 30 giorni: sono quelle da chiudere per prime.
 */
const STAFF_PHASES: PhaseOption[] = [
  { value: 'all', labelKey: 'requests.phaseAll', tone: 'neutral' },
  { value: 'to_assign', labelKey: 'requests.phaseToAssign', tone: 'requestToAssign' },
  { value: 'in_progress', labelKey: 'requests.phaseInProgress', tone: 'requestInProgress' },
  { value: 'imminent', labelKey: 'requests.phaseImminent', tone: 'requestImminent' },
];
const ADVISOR_PHASES: PhaseOption[] = [
  { value: 'all', labelKey: 'requests.phaseAll', tone: 'neutral' },
  { value: 'new', labelKey: 'requests.phaseNew', tone: 'requestNew' },
  { value: 'imminent', labelKey: 'requests.phaseImminent', tone: 'requestImminent' },
];

/** Giorno di calendario scelto dall'utente, senza passare da UTC. */
const toIsoDate = (date?: Date) => {
  if (!date) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** Filtri della toolbar nella forma che legge get_dashboard_travel_requests_page. */
const toServerFilters = (filters: FilterConfig[]): DashboardTravelRequestFilter[] =>
  filters.map((f) => ({
    field: f.field,
    operation: f.operation,
    value: f.value,
    from: toIsoDate(f.dateRange?.from),
    to: toIsoDate(f.dateRange?.to),
  }));

export const DashboardRequestsTab = ({
  view,
  userRole,
  refreshKey,
  onRefresh,
  onViewRequest,
  onCreateItinerary,
  i18n,
}: RequestsTabProps) => {
  const { t } = useI18n();
  // Solo le completate hanno un itinerario scelto da mostrare.
  const { openPreview, loadingId: chosenLoadingId } = useChosenItineraryPreview();
  const onViewChosenItinerary = view === 'completed' ? openPreview : undefined;
  const { user } = useAuth();
  const filterOptions = useMemo(() => buildFilterOptions(t), [t]);

  const [viewMode, setViewMode] = useDashboardViewMode();
  const [sortMode, setSortMode] = useState<SortMode>('newest');
  const [filters, setFilters] = useState<FilterConfig[]>([]);
  const [phase, setPhase] = useState<RequestPhaseFilter>('all');
  // Il cliente vede solo le sue richieste: per lui le fasi di lavorazione non dicono nulla.
  const showPhases = view === 'active' && userRole !== 'traveler';
  const phases = userRole === 'admin' || userRole === 'agency' ? STAFF_PHASES : ADVISOR_PHASES;
  // Richiesta aperta in modifica: il modale e' lo stesso della creazione.
  const [editingRequest, setEditingRequest] = useState<DashboardTravelRequest | null>(null);
  // Arrivando dal promemoria "Assegna" il modale si apre sul tab Assegnazione.
  const [editingOnAssignment, setEditingOnAssignment] = useState(false);

  // Ogni cambio di filtro o ordinamento riparte da pagina 1.
  const serverFilters = useMemo(() => toServerFilters(filters), [filters]);
  const filtersKey = JSON.stringify({ serverFilters, sortMode, phase });
  const [pageState, setPageState] = useState({ key: filtersKey, page: 1 });
  const page = pageState.key === filtersKey ? pageState.page : 1;
  const setPage = (next: number) => setPageState({ key: filtersKey, page: next });

  const { data, loading, refreshing, error, version } = useDashboardTravelRequestsPage(
    {
      view,
      phase: showPhases && phase !== 'all' ? phase : null,
      filters: serverFilters,
      sort: sortMode,
      limit: DASHBOARD_PAGE_SIZE,
      offset: (page - 1) * DASHBOARD_PAGE_SIZE,
    },
    refreshKey
  );
  const requests = data.rows;
  const hasFilters = filters.length > 0 || phase !== 'all';

  // Le pillole seguono i filtri della toolbar: con "destinazione: Giappone" il numero
  // accanto a "Nuove" deve contare solo quelle.
  const { data: phaseCounts } = useDashboardTravelRequestCounts(refreshKey, serverFilters, showPhases);
  const phaseCount = (value: RequestPhaseFilter) =>
    value === 'all' ? phaseCounts.byView.active ?? 0 : phaseCounts.byPhase[value] ?? 0;

  const clearFilters = () => {
    setFilters([]);
    setPhase('all');
  };

  // Chiudendo o completando l'ultima richiesta dell'ultima pagina, la pagina non esiste piu'.
  useEffect(() => {
    if (!loading && !refreshing && !error && requests.length === 0 && page > 1) setPage(page - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, refreshing, error, requests.length, page]);

  const toAssign = (req: DashboardTravelRequest) =>
    needsAgencyAssignment(req, userRole, user?.id);

  const openEdit = (req: DashboardTravelRequest) => {
    setEditingOnAssignment(toAssign(req));
    setEditingRequest(req);
  };

  // Vuota per i filtri o vuota davvero: due messaggi diversi.
  const emptyState = hasFilters ? (
    <div className="text-center py-12">
      <h4 className="text-gray-600 mb-4">{t('dashboard.noSearchResults')}</h4>
      <p className="text-gray-500 mb-4">{t('dashboard.noSearchResultsDesc')}</p>
      <Button variant="outline" onClick={clearFilters}>
        {t('dashboard.removeAllFilters')}
      </Button>
    </div>
  ) : (
    <div className="text-center py-12">
      <h4 className="text-gray-600 mb-4">{t(i18n.emptyTitleKey as any)}</h4>
      {i18n.emptyDescKey && <p className="text-gray-500 mb-4">{t(i18n.emptyDescKey as any)}</p>}
    </div>
  );

  return (
    // Altezza piena: intestazione fissa, la lista riempie il resto e la paginazione
    // resta ancorata in fondo anche con pochi risultati.
    <div className="flex flex-1 flex-col min-h-0 gap-4">
      {/* Header: a sinistra lo stato, a destra filtri e azioni sulla vista. */}
      <div className="shrink-0 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          {showPhases && (
            <StatusFilter<RequestPhaseFilter>
              label={String(t('dashboard.statusFilterLabel'))}
              value={phase}
              onChange={setPhase}
              options={phases.map((p) => ({
                value: p.value,
                label: String(t(p.labelKey)),
                count: phaseCount(p.value),
                tone: p.tone,
              }))}
            />
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <FilterTravelRequestButton
            filters={filters}
            onFiltersChange={setFilters}
            options={filterOptions}
            disabled={!hasFilters && data.totalCount === 0}
          />
          <SortTravelRequestButton
            sortMode={sortMode}
            onSortModeChange={setSortMode}
            disabled={data.totalCount === 0}
          />
          <ViewTravelRequestButton
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            disabled={data.totalCount === 0}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">{t('dashboard.refresh')}</span>
          </Button>
        </div>
      </div>

      {error ? (
        <div className="text-center py-12">
          <h4 className="text-red-600">{t('dashboard.loadError')}</h4>
          <Button onClick={onRefresh} className="mt-4">{t('dashboard.retry')}</Button>
        </div>
      ) : viewMode === 'grid' ? (
        <>
          <div className="relative flex-1 min-h-0 overflow-auto">
            {loading || refreshing ? (
              // In caricamento (anche aggiorna, pagina, filtri): card scheletro con le misure
              // vere per almeno LIST_MIN_LOADING_MS (useDashboardLists), poi le card entrano animate.
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-3">
                {Array.from({ length: 8 }, (_, i) => <TravelRequestCardSkeleton key={i} />)}
              </div>
            ) : requests.length > 0 ? (
              /* Margine interno: l'area scrolla, e senza spazio ai bordi taglierebbe l'hover
                  delle card (si alzano e si ingrandiscono) e le loro ombre. */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-3">
                {requests.map((travelRequest, index) => (
                  <div
                    key={`${version}-${travelRequest.id}`}
                    className="animate-in fade-in zoom-in-95 duration-500"
                    style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'backwards' }}
                  >
                    <TravelRequestCard
                      travelRequest={travelRequest}
                      userRole={userRole}
                      onViewRequest={() => onViewRequest(travelRequest)}
                      onCreateItinerary={() => onCreateItinerary(travelRequest)}
                      onEditRequest={
                        canEditRequest(travelRequest.status, userRole)
                          ? () => openEdit(travelRequest)
                          : undefined
                      }
                      needsAssignment={toAssign(travelRequest)}
                      onViewChosenItinerary={onViewChosenItinerary}
                      chosenItineraryLoading={
                        !!chosenLoadingId && chosenLoadingId === travelRequest.chosenItinerary?.id
                      }
                    />
                  </div>
                ))}
              </div>
            ) : emptyState}
          </div>
          <ListPagination
            loading={loading}
            page={page}
            pageSize={DASHBOARD_PAGE_SIZE}
            totalCount={data.totalCount}
            onPageChange={setPage}
            disabled={refreshing}
          />
        </>
      ) : (
        <div className="flex-1 min-h-0">
          <TravelRequestsTable
            travelRequests={requests}
            userRole={userRole}
            onRowClick={onViewRequest}
            onEditRequest={openEdit}
            canEditRequest={(req) => canEditRequest(req.status, userRole)}
            needsAssignment={toAssign}
            onViewChosenItinerary={onViewChosenItinerary}
            chosenItineraryLoadingId={chosenLoadingId}
            serverPagination={{
              page,
              totalItems: data.totalCount,
              onPageChange: setPage,
              disabled: refreshing,
            }}
            pageSize={DASHBOARD_PAGE_SIZE}
            // Skeleton anche ad ogni ricaricamento, non solo al primo
            isLoading={loading || refreshing}
            animationKey={version}
            emptyState={emptyState}
          />
        </div>
      )}

      {/* Modale richiesta cliente in modifica: la creazione sta nella testata */}
      <ClientRequestModal
        isOpen={!!editingRequest}
        mode="edit"
        request={editingRequest ?? undefined}
        initialTab={editingOnAssignment ? 'assignment' : undefined}
        onClose={() => {
          setEditingRequest(null);
          setEditingOnAssignment(false);
        }}
        onSuccess={onRefresh}
      />
    </div>
  );
};
